import { useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import Checkbox from "../checkbox/Checkbox";
import ContentHeader from "../contentHeader/ContentHeader";
import FilterTabs, { type FilterType } from "../filterTabs/FilterTabs";
import QuestionCard from "../questionCard/QuestionCard";
import SearchInput from "../searchInput/SearchInput";
import { useJobDetailsStore } from "../../stores/jobDetails.store";
import { useQuestionStore } from "../../stores/question.store";
import { useResources } from "../../hooks/useResources";
import { usePermissions } from "../../hooks/usePermissions";
import { useToast } from "../../contexts/ToastContext";
import { globalConfig } from "../../config/globalConfig";
import "./QuestionsList.scss";

const PAGE_SIZE = globalConfig.previewQuestionsPageSize;

interface QuestionListItem {
  id: string;
  questionNumber: number;
  status: "Published" | "Unpublished";
  apiStatus: string; // Original API status: "published", "passed", "pending_graphics", etc.
  fullText?: string;
  images?: string[];
  answerChoices?: string[];
}

const QuestionsList = () => {
  const { jobId: paramJobId } = useParams<{ jobId: string }>();
  const location = useLocation();
  const jobId = paramJobId || "";
  
  const { getResourceValue } = useResources();
  const { job, isLoading, error, load } = useJobDetailsStore();
  const { publishQuestions } = useQuestionStore();
  const { canPublishQuestions } = usePermissions();
  const { showSuccess, showError } = useToast();

  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  // Get initial filter and questionId from navigation state
  useEffect(() => {
    const state = location.state as { filter?: string; questionId?: string; jobId?: string } | null;
    if (state) {
      // Set filter if provided
      if (state.filter) {
        if (state.filter === "published" || state.filter === "pending_graphics" || state.filter === "unpublished") {
          setActiveFilter(state.filter as FilterType);
        }
      }
      
      // Set search query to questionId if provided (to filter and highlight the question)
      if (state.questionId) {
        setSearchQuery(state.questionId);
      }
      
      // Clear the state after reading it to avoid persisting on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Load job data - always fetch all questions for accurate counts
  // Filtering is done client-side to maintain correct counts across all tabs
  useEffect(() => {
    if (!jobId) return;
    // Always fetch all questions (no status filter)
    load(jobId, undefined);
  }, [jobId, load]);

  // Map API questions to component structure
  const questions: QuestionListItem[] = useMemo(() => {
    if (!job?.job_status_payload?.questions) return [];

    return job.job_status_payload.questions.map((q, index) => {
      // Map API status to component status
      // "published" -> "Published"
      // "passed", "pending_graphics" -> "Unpublished" (can be published)
      // "failed", "processing" -> "Unpublished" (but may not be publishable)
      const isPublished = q.status === "published";
      return {
        id: q.id,
        questionNumber: index + 1,
        status: isPublished ? "Published" : "Unpublished",
        apiStatus: q.status, // Preserve original API status for filtering
        fullText: undefined, // TODO: Add when API provides this
        images: q.has_graphics ? [] : undefined, // TODO: Add image URLs when API provides
        answerChoices: undefined, // TODO: Add when API provides
      };
    });
  }, [job]);

  // Filter questions based on active filter and search
  const filteredQuestions = useMemo(() => {
    let filtered = questions;

    // Apply status filter
    if (activeFilter === "published") {
      filtered = filtered.filter((q) => q.apiStatus === "published");
    } else if (activeFilter === "pending_graphics") {
      filtered = filtered.filter((q) => q.apiStatus === "pending_graphics");
    } else if (activeFilter === "unpublished") {
      // Unpublished includes "passed" status (excluding pending_graphics and published)
      filtered = filtered.filter((q) => q.apiStatus === "passed");
    }
    // "all" filter - no status filtering needed

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (q) =>
          q.id.toLowerCase().includes(query) ||
          q.questionNumber.toString().includes(query) ||
          q.fullText?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [questions, activeFilter, searchQuery]);

  // Calculate counts
  const counts = useMemo(() => {
    return {
      all: questions.length,
      published: questions.filter((q) => q.apiStatus === "published").length,
      unpublished: questions.filter((q) => q.apiStatus === "passed").length,
      pending_graphics: questions.filter((q) => q.apiStatus === "pending_graphics").length,
    };
  }, [questions]);

  // Paginate filtered questions
  const totalPages = Math.ceil(filteredQuestions.length / PAGE_SIZE);
  const paginatedQuestions = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    return filteredQuestions.slice(startIndex, endIndex);
  }, [filteredQuestions, currentPage]);

  // Reset to page 1 when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, searchQuery]);

  // Get selectable questions for current page (unpublished and pending_graphics can be published)
  const selectableQuestionsOnPage = useMemo(() => {
    return paginatedQuestions.filter(
      (q) => q.status === "Unpublished" || q.apiStatus === "pending_graphics"
    );
  }, [paginatedQuestions]);

  // Check if all selectable questions on current page are selected
  const areAllSelectedOnPage = useMemo(() => {
    if (selectableQuestionsOnPage.length === 0) return false;
    return selectableQuestionsOnPage.every((q) => selectedQuestionIds.has(q.id));
  }, [selectableQuestionsOnPage, selectedQuestionIds]);

  // Determine if publish button should be enabled
  const isPublishButtonEnabled = useMemo(() => {
    if (activeFilter === "published") return false;
    return selectedQuestionIds.size > 0;
  }, [selectedQuestionIds.size, activeFilter]);

  // Determine page title based on filter - show count of selectable questions on current page
  const pageTitle = useMemo(() => {
    if (activeFilter === "published") return "All Published Questions";
    if (activeFilter === "pending_graphics" || activeFilter === "unpublished") {
      // Show count of selectable questions on current page (max PAGE_SIZE)
      const selectableCount = selectableQuestionsOnPage.length;
      return `Select ${selectableCount} Questions`;
    }
    return "All Questions";
  }, [activeFilter, selectableQuestionsOnPage.length]);

  const handleQuestionSelect = (id: string, selected: boolean) => {
    setSelectedQuestionIds((prev) => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    // Only select/deselect unpublished questions on current page
    if (checked) {
      setSelectedQuestionIds((prev) => {
        const newSet = new Set(prev);
        selectableQuestionsOnPage.forEach((q) => newSet.add(q.id));
        return newSet;
      });
    } else {
      setSelectedQuestionIds((prev) => {
        const newSet = new Set(prev);
        selectableQuestionsOnPage.forEach((q) => newSet.delete(q.id));
        return newSet;
      });
    }
  };

  const handlePublish = async () => {
    if (!isPublishButtonEnabled || !jobId || selectedQuestionIds.size === 0) return;

    const questionIdsArray = Array.from(selectedQuestionIds);
    try {
      const response = await publishQuestions(jobId, questionIdsArray);
      // Clear selection and reload data
      setSelectedQuestionIds(new Set());
      // Always reload all questions to maintain accurate counts
      await load(jobId, undefined);
      
      // Show success message with details
      if (response.failed_count === 0) {
        showSuccess(
          getResourceValue("questions_published_successfully", "Questions Published Successfully"),
          getResourceValue(
            "questions_ready_to_use",
            `${response.published_count} questions are ready to use in your packages.`
          )
        );
      } else {
        // Build detailed error message with specific errors
        const failedDetails = response.details
          .filter((d) => d.status === "failed")
          .map((d) => `Question ${d.question_id}: ${d.error || "Failed to publish"}`)
          .join(", ");
        
        showError(
          getResourceValue("publish_partial_error", "Publish Partially Failed"),
          `${getResourceValue("published_count", "Published")}: ${response.published_count}, ${getResourceValue("failed_count", "Failed")}: ${response.failed_count}. ${failedDetails}`
        );
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
        getResourceValue("publish_failed", "Failed to publish questions. Please try again.");
      showError(
        getResourceValue("publish_error", "Publish Failed"),
        errorMessage
      );
      console.error("Error publishing questions:", error);
    }
  };

  const handlePublishQuestion = async (id: string) => {
    if (!jobId) return;

    try {
      const response = await publishQuestions(jobId, [id]);
      // Always reload all questions to maintain accurate counts
      await load(jobId, undefined);
      
      // Check if the specific question was published successfully
      const questionResult = response.details.find((d) => d.question_id === id);
      if (questionResult?.status === "published") {
        showSuccess(
          getResourceValue("question_published_successfully", "Question Published Successfully"),
          getResourceValue("question_ready_to_use", "The question is now ready to use in your packages.")
        );
      } else {
        // Show specific error message if available, otherwise show generic message
        const errorMessage = questionResult?.error || 
          getResourceValue("question_publish_failed", "Failed to publish question. Please try again.");
        showError(
          getResourceValue("publish_error", "Publish Failed"),
          errorMessage
        );
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
        getResourceValue("publish_failed", "Failed to publish question. Please try again.");
      showError(
        getResourceValue("publish_error", "Publish Failed"),
        errorMessage
      );
      console.error("Error publishing question:", error);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageClick = (page: number) => {
    setCurrentPage(page);
  };

  if (isLoading) {
    return (
      <div className="questions-list-loading">
        {/* {getResourceValue("loading_questions", "Loading questions...")} */}
      </div>
    );
  }

  if (error) {
    return (
      <div className="questions-list-error">
        {getResourceValue("error_prefix", "Error:")} {error}
      </div>
    );
  }

  if (!job) {
    return (
      <div className="questions-list-not-found">
        {getResourceValue("job_not_found", "Job not found")}
      </div>
    );
  }

  return (
    <div className="questions-list">
      <div className="questions-list__container">
        <ContentHeader
          title={jobId}
          headingLevel={1}
          actions={
            canPublishQuestions && (
              <button
                className="btn btn-green"
                onClick={handlePublish}
                disabled={!isPublishButtonEnabled}
              >
                <i className="f-icon upload-icon"></i>
                <span>
                  {getResourceValue("publish_questions", "Publish")} {selectedQuestionIds.size > 0 && `[${selectedQuestionIds.size}]`} {getResourceValue("questions", "Questions")}
                </span>
              </button>
            ) 
          }
          className="content-header--job-details-header"
        />

        <div className="questions-list__controls">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={getResourceValue("search", "Search")}
            className="questions-list__search"
          />
          <FilterTabs
            activeFilter={activeFilter}
            counts={counts}
            onFilterChange={setActiveFilter}
          />
        </div>

        {(activeFilter === "unpublished" || activeFilter === "pending_graphics") && canPublishQuestions && (
          <div className="questions-list__title-wrapper">
            <Checkbox
              id="select-all-questions"
              checked={areAllSelectedOnPage}
              onChange={handleSelectAll}
            />
            <h2 className="questions-list__title">{pageTitle}</h2>
          </div>
        )}

        <div className="questions-list__content">
          {paginatedQuestions.length > 0 ? (
            paginatedQuestions.map((question) => (
              <QuestionCard
                key={question.id}
                id={question.id}
                questionNumber={question.questionNumber}
                status={question.status}
                jobId={jobId}
                isSelected={selectedQuestionIds.has(question.id)}
                showCheckbox={
                  canPublishQuestions && (
                    activeFilter === "unpublished" ||
                    activeFilter === "pending_graphics" ||
                    activeFilter === "all"
                  )
                }
                onSelect={handleQuestionSelect}
                onPublish={handlePublishQuestion}
              />
            ))
          ) : (
            <div className="questions-list__empty">
              {getResourceValue("no_questions_found", "No questions found")}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="questions-list__pagination">
            <button
              className="questions-list__pagination-btn"
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              <i className="f-icon chevron-left-icon"></i>
              {getResourceValue("previous")}
            </button>

            <div className="questions-list__pagination-pages">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className={`questions-list__pagination-page ${
                    currentPage === page ? "questions-list__pagination-page--active" : ""
                  }`}
                  onClick={() => handlePageClick(page)}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              className="questions-list__pagination-btn"
              onClick={handleNextPage}
              disabled={currentPage >= totalPages}
              aria-label="Next page"
            >
              {getResourceValue("next")}
              <i className="f-icon chevron-right-icon"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionsList;
